import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { PAYMENTS_SECTION_ID } from '../../utils/constants';
import styles from './CreateDeliveryDetailedPage.module.less';
import { connect } from 'react-redux';
import PropTypes, { number } from 'prop-types';
import classnames from 'classnames';
import Page from '../../components/Page/Page';
import TitleIcon from '../../assets/img/titleIcon.svg?react';
import Title from '../../components/Page/Title';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import {
  bookingChargeToCardAlertChangePaymentMethodClicked,
  bookingChargeToCardAlertDisplayed,
  bookingChargeToCardAlertTopUpClicked,
  negativeWalletBalanceAlertDisplayed,
  negativeWalletBalanceAlertTopUpClicked,
  requestDeliveryScreenOpen,
  zeroWalletBalanceAlertDisplayed,
  zeroWalletBalanceAlertTopUpClicked
} from '../../utils/analytika/events';
import Banner from '../../components/Banner/Banner';
import Button from '../../components/Button/Button';
import AddAmountModal from '../../components/AddAmountModal/AddAmountModal';
import { getDisplayUserBalance } from '../../utils/numbers';
import { useHistory } from 'react-router-dom';
import { routes } from '../../utils/routerHelper';
import { PaymentType } from '../../api/models/payments';
import { ANALYTIKA_ACTIONS, ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import CreateDeliveryFormv2 from './CreateDeliveryFormv2';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';

const { useBreakpoint } = Grid;

export const CreateDeliveryDetailedPage = props => {
  const { t } = useTranslation();
  const { push: historyPush } = useHistory();
  const {
    userBalance,
    createWalletInvoice,
    defaultPaymentMethodId,
    paymentMethods,
    isBlockingRequestsComplete,
    isZeroWalletBalance,
    isWalletCreditUsed,
    secondaryPaymentMethodId,
    merchantConfig
  } = props;
  const [isAmountModalVisible, setIsAmountModalVisible] = useState(false);

  const {
    availablePaymentMethods: { isWalletEnabled }
  } = merchantConfig;

  const { fireEvent } = useAnalytika();

  const { md: isMdBreakpoint } = useBreakpoint();
  const { currency, amount } = userBalance;

  const balanceDisplay = getDisplayUserBalance(userBalance, false);

  const defaultPaymentType = paymentMethods.find(method => method.id === defaultPaymentMethodId)?.paymentType;
  const isCardDefault = defaultPaymentType === PaymentType.CREDIT_CARD;

  const secondaryPayment = paymentMethods.find(method => method.id === secondaryPaymentMethodId);
  const secondaryPaymentType = secondaryPayment?.paymentType;
  const isCardSecondary = secondaryPaymentType === PaymentType.CREDIT_CARD;
  const secondaryPaymentCardDigits = isCardSecondary && secondaryPayment.display.substring(15);

  const isNegativeBalance = Boolean(amount < 0);

  const isErrorNegativeBalanceCardBooking = isBlockingRequestsComplete && Boolean(isCardDefault && isNegativeBalance);

  const isWarningZeroBalanceNoPayment =
    isBlockingRequestsComplete &&
    Boolean(isWalletEnabled && paymentMethods && !paymentMethods.length && isZeroWalletBalance);

  const isWarningZeroBalanceCardSecondary =
    isBlockingRequestsComplete && Boolean(isWalletCreditUsed && isCardSecondary && isZeroWalletBalance);

  const isBookingBlocked = isErrorNegativeBalanceCardBooking || isWarningZeroBalanceNoPayment;

  useEffect(() => {
    fireEvent(requestDeliveryScreenOpen({ active_deliveries_count: null }, ANALYTIKA_ACTIONS.PAGE_OPEN));
  }, []);

  useEffect(() => {
    if (isErrorNegativeBalanceCardBooking) {
      fireEvent(negativeWalletBalanceAlertDisplayed());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_NEG_BALANCE, event_version: 1 });
    }
    if (isWarningZeroBalanceNoPayment) {
      fireEvent(zeroWalletBalanceAlertDisplayed());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_ZERO_BALANCE, event_version: 1 });
    }
    if (isWarningZeroBalanceCardSecondary) {
      fireEvent(bookingChargeToCardAlertDisplayed());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_CHARGE_TO_CARD, event_version: 1 });
    }
  }, [isErrorNegativeBalanceCardBooking, isWarningZeroBalanceNoPayment, isWarningZeroBalanceCardSecondary]);

  const openAmountModal = useCallback(() => {
    if (isErrorNegativeBalanceCardBooking) {
      fireEvent(negativeWalletBalanceAlertTopUpClicked());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_NEG_BALANCE, event_version: 1 });
    }
    if (isWarningZeroBalanceNoPayment) {
      fireEvent(zeroWalletBalanceAlertTopUpClicked());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_ZERO_BALANCE, event_version: 1 });
    }
    if (isWarningZeroBalanceCardSecondary) {
      fireEvent(bookingChargeToCardAlertTopUpClicked());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_CHARGE_TO_CARD, event_version: 1 });
    }
    setIsAmountModalVisible(true);
  }, [isErrorNegativeBalanceCardBooking, isWarningZeroBalanceNoPayment, isWarningZeroBalanceCardSecondary]);

  const closeAmountModal = () => {
    setIsAmountModalVisible(false);
  };

  return (
    <Page>
      <Row type="flex" className={styles.content}>
        <Col xs={24} md={24} xxl={24} className={classnames([styles.fullHeight, styles.formContainer])}>
          {isMdBreakpoint && <Title text={t('deliveries.pageTitle')} Icon={TitleIcon} />}
          <div className={styles.form}>
            {isErrorNegativeBalanceCardBooking && (
              <Banner
                message={
                  <div className={styles.bannerTitleContent}>
                    <span>{t('deliveries.bannerNegativeBalance.message')}</span>
                    <Button size={'small'} type={'default'} onClick={openAmountModal}>
                      {t('deliveries.bannerNegativeBalance.topupButton')}
                    </Button>
                  </div>
                }
                description={t('deliveries.bannerNegativeBalance.description', { amount: balanceDisplay })}
                type={'error'}
                showIcon
                icon={<ExclamationCircleOutlined />}
              />
            )}
            {isWarningZeroBalanceNoPayment && (
              <Banner
                message={
                  <div className={styles.bannerTitleContent}>
                    <span>{t('deliveries.bannerZeroBalance.message')}</span>
                    <Button size={'small'} type={'default'} onClick={openAmountModal}>
                      {t('deliveries.bannerZeroBalance.topupButton')}
                    </Button>
                  </div>
                }
                description={t('deliveries.bannerZeroBalance.description')}
                type={'warning'}
                showIcon
                icon={<ExclamationCircleOutlined />}
              />
            )}
            {isWarningZeroBalanceCardSecondary && (
              <Banner
                message={
                  <div className={styles.bannerTitleContent}>
                    <span>{t('deliveries.bannerZeroBalanceCardBooking.message')}</span>
                    <Button size={'small'} type={'default'} onClick={openAmountModal}>
                      {t('deliveries.bannerZeroBalanceCardBooking.topupButton')}
                    </Button>
                  </div>
                }
                description={
                  <>
                    <div>
                      {t('deliveries.bannerZeroBalanceCardBooking.description_part_card', {
                        cardNumber: secondaryPaymentCardDigits
                      })}
                    </div>
                    <div>
                      <span>{t('deliveries.bannerZeroBalanceCardBooking.description_part_redirect_normal')}</span>
                      <a
                        onClick={() => {
                          fireEvent(bookingChargeToCardAlertChangePaymentMethodClicked());
                          historyPush(`${routes.profile}#${PAYMENTS_SECTION_ID}`);
                        }}
                      >
                        {t('deliveries.bannerZeroBalanceCardBooking.description_part_redirect_highligted')}
                      </a>
                    </div>
                  </>
                }
                type={'info'}
                showIcon
                icon={<InfoCircleOutlined />}
              />
            )}
            <CreateDeliveryFormv2 isBlocked={isBookingBlocked} />
          </div>
        </Col>
      </Row>
      <AddAmountModal
        currency={currency}
        onClose={closeAmountModal}
        visible={isAmountModalVisible}
        createWalletInvoice={createWalletInvoice}
      />
    </Page>
  );
};

CreateDeliveryDetailedPage.propTypes = {
  setDeliveryFormState: PropTypes.func,
  createDeliveryForm: PropTypes.object,
  userData: PropTypes.object,
  history: PropTypes.object,
  createWalletInvoice: PropTypes.func,
  userBalance: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string,
    fractionDigits: PropTypes.number
  }),
  defaultPaymentMethodId: number,
  paymentMethods: PropTypes.array,
  isBlockingRequestsComplete: PropTypes.bool,
  isZeroWalletBalance: PropTypes.bool,
  secondaryPaymentMethodId: PropTypes.number,
  isWalletCreditUsed: PropTypes.bool,
  userGalileoVars: PropTypes.object,
  merchantConfig: PropTypes.object
};
const mapDispatch = ({ deliveries: { setDeliveryFormState }, invoices: { createWalletInvoice } }) => ({
  setDeliveryFormState,
  createWalletInvoice
});

const mapState = ({
  deliveries: { createDeliveryForm },
  user: {
    userData,
    userWalletConfig: { userBalance, isZeroWalletBalance },
    defaultPaymentMethodId,
    paymentMethods,
    loadCompleteFor: { blockingRequests: isBlockingRequestsComplete },
    secondaryPaymentMethodId,
    isWalletCreditUsed,
    userGalileoVars,
    merchantConfig
  }
}) => ({
  createDeliveryForm,
  userData,
  userBalance,
  defaultPaymentMethodId,
  paymentMethods,
  isBlockingRequestsComplete,
  isZeroWalletBalance,
  secondaryPaymentMethodId,
  isWalletCreditUsed,
  userGalileoVars,
  merchantConfig
});

export default connect(mapState, mapDispatch)(CreateDeliveryDetailedPage);
